import { useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import type {
  LandSearchFilters,
  LandSearchSort,
} from '../types/landFilters.types'
import {
  applyDefaultFiltersIfNeeded,
  DEFAULT_LAND_FILTERS,
} from '../utils/defaultFilters'

type ViewType = 'list' | 'map' | 'stats'

interface UrlState {
  filters: LandSearchFilters
  view: ViewType
  sort: LandSearchSort[]
  page: number
  pageSize: number
}

// Helper to serialize filters to URL params
const filtersToUrlParams = (filters: LandSearchFilters): URLSearchParams => {
  const params = new URLSearchParams()

  // Simple string fields
  if (filters.location_search)
    params.set('location_search', filters.location_search)
  if (filters.location_type) params.set('location_type', filters.location_type)
  if (filters.municipality) params.set('municipality', filters.municipality)
  if (filters.district) params.set('district', filters.district)
  if (filters.region) params.set('region', filters.region)
  if (filters.npa) params.set('npa', filters.npa)
  if (filters.plan) params.set('plan', filters.plan)
  if (filters.assignment) params.set('assignment', filters.assignment)

  // Zone filters - exclude default values
  if (
    filters.special_zone &&
    filters.special_zone !== DEFAULT_LAND_FILTERS.special_zone
  ) {
    params.set('special_zone', filters.special_zone)
  }
  if (
    filters.no_building_zone &&
    filters.no_building_zone !== DEFAULT_LAND_FILTERS.no_building_zone
  ) {
    params.set('no_building_zone', filters.no_building_zone)
  }
  if (
    filters.bare_land &&
    filters.bare_land !== DEFAULT_LAND_FILTERS.bare_land
  ) {
    params.set('bare_land', String(filters.bare_land))
  }

  // Ownership filters
  if (filters.ownership_type)
    params.set('ownership_type', filters.ownership_type)
  if (
    filters.owners_type &&
    filters.owners_type !== DEFAULT_LAND_FILTERS.owners_type
  ) {
    params.set('owners_type', filters.owners_type)
  }

  // Range fields
  if (filters.area?.min !== undefined)
    params.set('area_min', filters.area.min.toString())
  if (filters.area?.max !== undefined)
    params.set('area_max', filters.area.max.toString())
  if (filters.owners?.min !== undefined)
    params.set('owners_min', filters.owners.min.toString())
  if (filters.owners?.max !== undefined)
    params.set('owners_max', filters.owners.max.toString())
  if (filters.development_score?.min !== undefined)
    params.set(
      'development_score_min',
      filters.development_score.min.toString()
    )
  if (filters.development_score?.max !== undefined)
    params.set(
      'development_score_max',
      filters.development_score.max.toString()
    )
  if (filters.under_exploited_score?.min !== undefined)
    params.set(
      'under_exploited_score_min',
      filters.under_exploited_score.min.toString()
    )
  if (filters.under_exploited_score?.max !== undefined)
    params.set(
      'under_exploited_score_max',
      filters.under_exploited_score.max.toString()
    )

  if (filters.hide_unknown_year !== undefined) {
    params.set('hide_unknown_year', String(filters.hide_unknown_year))
  }

  if (filters.sp_authorized?.min !== undefined)
    params.set('sp_authorized_min', filters.sp_authorized.min.toString())
  if (filters.sp_authorized?.max !== undefined)
    params.set('sp_authorized_max', filters.sp_authorized.max.toString())

  if (filters.sp_difference?.min !== undefined)
    params.set('sp_difference_min', filters.sp_difference.min.toString())
  if (filters.sp_difference?.max !== undefined)
    params.set('sp_difference_max', filters.sp_difference.max.toString())

  if (filters.volume?.min !== undefined)
    params.set('volume_min', filters.volume.min.toString())
  if (filters.volume?.max !== undefined)
    params.set('volume_max', filters.volume.max.toString())
  if (filters.building_area?.min !== undefined)
    params.set('building_area_min', filters.building_area.min.toString())
  if (filters.building_area?.max !== undefined)
    params.set('building_area_max', filters.building_area.max.toString())
  if (filters.building_year?.min !== undefined)
    params.set('building_year_min', filters.building_year.min.toString())
  if (filters.building_year?.max !== undefined)
    params.set('building_year_max', filters.building_year.max.toString())
  if (filters.renovation_year?.min !== undefined)
    params.set('renovation_year_min', filters.renovation_year.min.toString())
  if (filters.renovation_year?.max !== undefined)
    params.set('renovation_year_max', filters.renovation_year.max.toString())
  if (filters.floor_number?.min !== undefined)
    params.set('floorNumber_min', filters.floor_number.min.toString())
  if (filters.floor_number?.max !== undefined)
    params.set('floorNumber_max', filters.floor_number.max.toString())
  if (filters.building_height?.min !== undefined)
    params.set('building_height_min', filters.building_height.min.toString())
  if (filters.building_height?.max !== undefined)
    params.set('building_height_max', filters.building_height.max.toString())
  if (filters.purchase_price?.min !== undefined)
    params.set('purchase_price_min', filters.purchase_price.min.toString())
  if (filters.purchase_price?.max !== undefined)
    params.set('purchase_price_max', filters.purchase_price.max.toString())
  if (filters.rent_price?.min !== undefined)
    params.set('rent_price_min', filters.rent_price.min.toString())
  if (filters.rent_price?.max !== undefined)
    params.set('rent_price_max', filters.rent_price.max.toString())

  // Date ranges
  if (filters.transaction_date?.min)
    params.set('transaction_date_min', filters.transaction_date.min)
  if (filters.transaction_date?.max)
    params.set('transaction_date_max', filters.transaction_date.max)

  if (filters.construction_permit_date?.min)
    params.set(
      'construction_permit_date_min',
      filters.construction_permit_date.min
    )
  if (filters.construction_permit_date?.max)
    params.set(
      'construction_permit_date_max',
      filters.construction_permit_date.max
    )

  // Building permit classification filter
  if (filters.construction_permits_classification?.length) {
    params.set(
      'construction_permits_classification',
      filters.construction_permits_classification.join(',')
    )
  }

  // Tag filters
  if (filters.tags?.length) {
    params.set('tags', filters.tags.join(','))
  }
  if (filters.land_ids?.length) {
    params.set('land_ids', filters.land_ids.join(','))
  }

  if (filters.rent_date?.min) {
    params.set('rent_date_min', filters.rent_date.min)
  }
  if (filters.rent_date?.max) {
    params.set('rent_date_max', filters.rent_date.max)
  }

  if (filters.purchase_date?.min) {
    params.set('purchase_date_min', filters.purchase_date.min)
  }
  if (filters.purchase_date?.max) {
    params.set('purchase_date_max', filters.purchase_date.max)
  }
  if (filters.transaction_price?.min) {
    params.set(
      'transaction_price_min',
      filters.transaction_price.min.toString()
    )
  }
  if (filters.transaction_price?.max) {
    params.set(
      'transaction_price_max',
      filters.transaction_price.max.toString()
    )
  }

  return params
}

// Helper to parse URL params to filters
const urlParamsToFilters = (params: URLSearchParams): LandSearchFilters => {
  const filters: LandSearchFilters = {}

  // Simple fields
  if (params.has('location_search'))
    filters.location_search = params.get('location_search') || ''
  if (params.has('location_type'))
    filters.location_type = params.get('location_type') as
      | 'region'
      | 'district'
      | 'municipality'
      | 'npa'
  if (params.has('municipality'))
    filters.municipality = params.get('municipality') || ''
  // Don't parse municipality_id from URL - it's only for internal use
  if (params.has('district')) filters.district = params.get('district') || ''
  if (params.has('region')) filters.region = params.get('region') || ''
  if (params.has('npa')) filters.npa = params.get('npa') || ''
  if (params.has('plan')) filters.plan = params.get('plan') || ''
  if (params.has('assignment'))
    filters.assignment = params.get('assignment') || ''

  // Zone filters
  if (params.has('special_zone'))
    filters.special_zone = params.get('special_zone') || ''
  if (params.has('no_building_zone'))
    filters.no_building_zone = params.get('no_building_zone') || ''
  if (params.has('bare_land')) filters.bare_land = params.get('bare_land') || ''

  // Ownership filters
  if (params.has('ownership_type'))
    filters.ownership_type = params.get('ownership_type') || ''
  if (params.has('owners_type'))
    filters.owners_type = params.get('owners_type') || ''

  // Range fields
  if (params.has('area_min') || params.has('area_max')) {
    filters.area = {}
    if (params.has('area_min'))
      filters.area.min = Number(params.get('area_min'))
    if (params.has('area_max'))
      filters.area.max = Number(params.get('area_max'))
  }
  if (params.has('owners_min') || params.has('owners_max')) {
    filters.owners = {}
    if (params.has('owners_min'))
      filters.owners.min = Number(params.get('owners_min'))
    if (params.has('owners_max'))
      filters.owners.max = Number(params.get('owners_max'))
  }
  if (
    params.has('development_score_min') ||
    params.has('development_score_max')
  ) {
    filters.development_score = {}
    if (params.has('development_score_min'))
      filters.development_score.min = Number(
        params.get('development_score_min')
      )
    if (params.has('development_score_max'))
      filters.development_score.max = Number(
        params.get('development_score_max')
      )
  }
  if (
    params.has('under_exploited_score_min') ||
    params.has('under_exploited_score_max')
  ) {
    filters.under_exploited_score = {}
    if (params.has('under_exploited_score_min'))
      filters.under_exploited_score.min = Number(
        params.get('under_exploited_score_min')
      )
    if (params.has('under_exploited_score_max'))
      filters.under_exploited_score.max = Number(
        params.get('under_exploited_score_max')
      )
  }
  if (params.has('building_area_min') || params.has('building_area_max')) {
    filters.building_area = {}
    if (params.has('building_area_min'))
      filters.building_area.min = Number(params.get('building_area_min'))
    if (params.has('building_area_max'))
      filters.building_area.max = Number(params.get('building_area_max'))
  }
  if (params.has('building_year_min') || params.has('building_year_max')) {
    filters.building_year = {}
    if (params.has('building_year_min'))
      filters.building_year.min = Number(params.get('building_year_min'))
    if (params.has('building_year_max'))
      filters.building_year.max = Number(params.get('building_year_max'))
  }
  if (params.has('renovation_year_min') || params.has('renovation_year_max')) {
    filters.renovation_year = {}
    if (params.has('renovation_year_min'))
      filters.renovation_year.min = Number(params.get('renovation_year_min'))
    if (params.has('renovation_year_max'))
      filters.renovation_year.max = Number(params.get('renovation_year_max'))
  }
  if (params.has('volume_min') || params.has('volume_max')) {
    filters.volume = {}
    if (params.has('volume_min'))
      filters.volume.min = Number(params.get('volume_min'))
    if (params.has('volume_max'))
      filters.volume.max = Number(params.get('volume_max'))
  }
  if (params.has('floorNumber_min') || params.has('floorNumber_max')) {
    filters.floor_number = {}
    if (params.has('floorNumber_min'))
      filters.floor_number.min = Number(params.get('floorNumber_min'))
    if (params.has('floorNumber_max'))
      filters.floor_number.max = Number(params.get('floorNumber_max'))
  }

  if (params.has('building_height_min') || params.has('building_height_max')) {
    filters.building_height = {}
    if (params.has('building_height_min'))
      filters.building_height.min = Number(params.get('building_height_min'))
    if (params.has('building_height_max'))
      filters.building_height.max = Number(params.get('building_height_max'))
  }
  if (params.has('purchase_price_min') || params.has('purchase_price_max')) {
    filters.purchase_price = {}
    if (params.has('purchase_price_min'))
      filters.purchase_price.min = Number(params.get('purchase_price_min'))
    if (params.has('purchase_price_max'))
      filters.purchase_price.max = Number(params.get('purchase_price_max'))
  }
  if (params.has('rent_price_min') || params.has('rent_price_max')) {
    filters.rent_price = {}
    if (params.has('rent_price_min'))
      filters.rent_price.min = Number(params.get('rent_price_min'))
    if (params.has('rent_price_max'))
      filters.rent_price.max = Number(params.get('rent_price_max'))
  }

  // Date ranges
  if (
    params.has('transaction_date_min') ||
    params.has('transaction_date_max')
  ) {
    filters.transaction_date = {}
    if (params.has('transaction_date_min'))
      filters.transaction_date.min = params.get('transaction_date_min') || ''
    if (params.has('transaction_date_max'))
      filters.transaction_date.max = params.get('transaction_date_max') || ''
  }
  if (
    params.has('construction_permit_date_min') ||
    params.has('construction_permit_date_max')
  ) {
    filters.construction_permit_date = {}
    if (params.has('construction_permit_date_min'))
      filters.construction_permit_date.min =
        params.get('construction_permit_date_min') || ''
    if (params.has('construction_permit_date_max'))
      filters.construction_permit_date.max =
        params.get('construction_permit_date_max') || ''
  }

  // Building permits classification
  if (params.has('construction_permits_classification')) {
    const classifications = params.get('construction_permits_classification')
    if (classifications) {
      filters.construction_permits_classification = classifications.split(',')
    }
  }

  // Tag filters
  if (params.has('tags')) {
    const tags = params.get('tags')
    if (tags) {
      filters.tags = tags.split(',')
    }
  }
  if (params.has('land_ids')) {
    const landIds = params.get('land_ids')
    if (landIds) {
      filters.land_ids = landIds.split(',')
    }
  }

  if (params.has('hide_unknown_year')) {
    filters.hide_unknown_year = params.get('hide_unknown_year') === 'true'
  }

  if (params.has('sp_authorized_min') || params.has('sp_authorized_max')) {
    filters.sp_authorized = {}
    if (params.has('sp_authorized_min'))
      filters.sp_authorized.min = Number(params.get('sp_authorized_min'))
    if (params.has('sp_authorized_max'))
      filters.sp_authorized.max = Number(params.get('sp_authorized_max'))
  }

  if (params.has('sp_difference_min') || params.has('sp_difference_max')) {
    filters.sp_difference = {}
    if (params.has('sp_difference_min'))
      filters.sp_difference.min = Number(params.get('sp_difference_min'))
    if (params.has('sp_difference_max'))
      filters.sp_difference.max = Number(params.get('sp_difference_max'))
  }

  if (params.has('rent_date_min') || params.has('rent_date_max')) {
    filters.rent_date = {}
    if (params.has('rent_date_min'))
      filters.rent_date.min = params.get('rent_date_min') || ''
    if (params.has('rent_date_max'))
      filters.rent_date.max = params.get('rent_date_max') || ''
  }

  if (params.has('purchase_date_min') || params.has('purchase_date_max')) {
    filters.purchase_date = {}
    if (params.has('purchase_date_min'))
      filters.purchase_date.min = params.get('purchase_date_min') || ''
    if (params.has('purchase_date_max'))
      filters.purchase_date.max = params.get('purchase_date_max') || ''
  }

  if (
    params.has('transaction_price_min') ||
    params.has('transaction_price_max')
  ) {
    filters.transaction_price = {}
    if (params.has('transaction_price_min'))
      filters.transaction_price.min = Number(
        params.get('transaction_price_min')
      )
    if (params.has('transaction_price_max'))
      filters.transaction_price.max = Number(
        params.get('transaction_price_max')
      )
  }

  return filters
}

export const useLandsUrl = (propertyPanelOpen: boolean = false) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Parse current URL state
  const getUrlState = useCallback((): UrlState => {
    const params = new URLSearchParams(location.search)

    // Parse filters from URL
    const parsedFilters = urlParamsToFilters(params)

    // Apply default filters if no active filters and no URL filter parameters
    const filters = applyDefaultFiltersIfNeeded(parsedFilters, params)

    // Parse view
    const view = (params.get('view') as ViewType) || 'list'

    // Parse sort
    const sort: LandSearchSort[] = []
    const sortField = params.get('sort_field')
    const sortOrder = params.get('sort_order') as 'asc' | 'desc' | null
    if (sortField && sortOrder) {
      sort.push({ field: sortField, order: sortOrder })
    }

    // Parse pagination
    const page = Number(params.get('page')) || 1
    const pageSize = Number(params.get('page_size')) || 20

    return { filters, view, sort, page, pageSize }
  }, [location.search])

  // Update URL with new state
  const updateUrl = useCallback(
    (updates: Partial<UrlState>) => {
      const currentState = getUrlState()
      const newState = { ...currentState, ...updates }

      // Build new URL params, preserving existing non-land params
      const currentParams = new URLSearchParams(location.search)
      const params = new URLSearchParams()

      // Preserve important global parameters (like mode, map view mode, etc.)
      // Also preserve building sort params so they aren't lost when updating lands URL
      const globalParamsToPreserve = [
        'mode',
        'dimension',
        'basemap',
        'activeLayer',
        'more',
        'transport',
        'duration',
        'lat',
        'lon',
        'zoom',
        'building_sort_field', // Buildings sort field
        'building_sort_order', // Buildings sort order
        // Only preserve landId if panel is open (to allow closePropertyPanel to remove it)
        ...(propertyPanelOpen ? ['landId'] : []),
      ]

      globalParamsToPreserve.forEach((param) => {
        if (currentParams.has(param)) {
          params.set(param, currentParams.get(param)!)
        }
      })

      // Add filters
      const filterParams = filtersToUrlParams(newState.filters)
      filterParams.forEach((value, key) => params.set(key, value))

      // Add view
      if (newState.view !== 'list') params.set('view', newState.view)

      // Add sort
      if (newState.sort.length > 0) {
        params.set('sort_field', newState.sort[0].field)
        params.set('sort_order', newState.sort[0].order)
      }

      // Add pagination
      if (newState.page > 1) params.set('page', newState.page.toString())
      if (newState.pageSize !== 20)
        params.set('page_size', newState.pageSize.toString())

      // Update URL without triggering navigation
      const newSearch = params.toString()
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname

      navigate(newUrl, { replace: true })
    },
    [navigate, location.pathname, location.search, getUrlState, propertyPanelOpen]
  )

  // Individual update methods
  const updateFilters = useCallback(
    (filters: LandSearchFilters) => {
      updateUrl({ filters })
    },
    [updateUrl]
  )

  const updateView = useCallback(
    (view: ViewType) => {
      updateUrl({ view })
    },
    [updateUrl]
  )

  const updateSort = useCallback(
    (sort: LandSearchSort[]) => {
      updateUrl({ sort })
    },
    [updateUrl]
  )

  const updatePagination = useCallback(
    (page: number, pageSize: number) => {
      updateUrl({ page, pageSize })
    },
    [updateUrl]
  )

  return {
    urlState: getUrlState(),
    updateFilters,
    updateView,
    updateSort,
    updatePagination,
    updateUrl,
  }
}
